import { useState } from "react";
import { toast } from "sonner";
import {
  FileText, Sheet, Lock, ArrowRight, Download, Sparkles, CheckCircle2, Mail, User, X, ShieldCheck,
} from "lucide-react";
import { site } from "../lib/siteConfig";

const API = process.env.REACT_APP_BACKEND_URL;

const resources = [
  {
    id: "guida-fiscalita-2026",
    kicker: "01 · Guida + Calcolatore",
    title: "Fiscalità Affitti",
    titleItalic: "in Italia · 2026",
    description:
      "Panoramica normativa completa (Cedolare 21% e 10%, IRPEF, IMU, transitorio) + calcolatore Excel interattivo che confronta la rendita netta nei 4 scenari: Breve · Transitorio · 4+4 · Vendita.",
    badge: "Gratis",
    price: null,
    files: [
      { icon: FileText, label: "Guida PDF", size: "8 pagine · A4" },
      { icon: Sheet, label: "Calcolatore Excel", size: "3 fogli · formule automatiche" },
    ],
    highlights: [
      "Confronto affiancato dei 4 regimi fiscali",
      "Calcolatore rendita netta annua/mensile + yield",
      "Esempio pratico bilocale Milano (60 mq · € 350.000)",
      "Consigli operativi su IMU, sconto 25% e canone concordato",
    ],
    cta: "Scarica gratis",
    available: true,
  },
  {
    id: "coming-soon-1",
    kicker: "02 · In arrivo",
    title: "Playbook",
    titleItalic: "affitto transitorio",
    description:
      "Come strutturare un contratto transitorio a canone concordato: fac-simile, tempistiche, prezzo giusto, come selezionare l'inquilino.",
    badge: "In arrivo",
    price: "€ 39",
    files: [
      { icon: FileText, label: "Ebook PDF" },
      { icon: Sheet, label: "Fac-simile contratto" },
    ],
    highlights: [
      "Modello di contratto commentato",
      "Checklist operativa 12 punti",
      "Strategie di prezzo per Milano e area laghi",
    ],
    cta: "Notificami all'uscita",
    available: false,
  },
];

/* ------------------ EMAIL GATE MODAL ------------------ */
const EmailGate = ({ resource, onClose }) => {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [consenso, setConsenso] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // {pdf, xlsx}

  const submit = async (e) => {
    e.preventDefault();
    if (nome.trim().length < 2 || !email.includes("@")) {
      toast.error("Nome ed email sono obbligatori");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/lead-magnet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome,
          email,
          resource_id: resource.id,
          consenso_marketing: consenso,
          website,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Errore durante il download");
      }
      const data = await res.json();
      setDone(data.downloads);
      toast.success("Perfetto! I file sono pronti · trovi tutto anche via email.");
    } catch (err) {
      toast.error(err.message || "Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="email-gate-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-brand-bg max-w-lg w-full rounded-sm shadow-2xl overflow-hidden animate-in fade-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="email-gate-close"
          className="absolute top-4 right-4 p-2 text-brand-muted hover:text-brand-ink transition-colors z-10"
          aria-label="Chiudi"
        >
          <X size={18} />
        </button>

        {!done ? (
          <>
            <div className="bg-brand-ink text-white px-8 pt-10 pb-8">
              <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-brand-beige">
                <span className="inline-block align-middle h-px bg-brand-beige/70 w-10 mr-2" />
                {resource.kicker}
              </div>
              <h3 className="mt-3 font-display text-3xl leading-[1.05] font-black">
                {resource.title}
                <br />
                <span className="italic font-normal">{resource.titleItalic}</span>
              </h3>
            </div>

            <form onSubmit={submit} className="px-8 py-7 space-y-5">
              <p className="text-sm text-brand-muted leading-relaxed">
                Lascia il tuo <strong className="text-brand-ink">nome ed email</strong> e ricevi
                subito il PDF + Excel. Nessuno spam — solo contenuti utili quando ne pubblico di nuovi.
              </p>

              {/* honeypot */}
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />

              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] font-semibold text-brand-muted flex items-center gap-1.5">
                  <User size={11} /> Nome
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  data-testid="lm-nome"
                  autoFocus
                  required
                  minLength={2}
                  className="mt-2 w-full border-0 border-b border-brand-line bg-transparent py-2 focus:border-brand-ink focus:outline-none font-display text-lg text-brand-ink placeholder:text-brand-muted/50"
                  placeholder="Come ti chiami?"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] font-semibold text-brand-muted flex items-center gap-1.5">
                  <Mail size={11} /> Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="lm-email"
                  required
                  className="mt-2 w-full border-0 border-b border-brand-line bg-transparent py-2 focus:border-brand-ink focus:outline-none font-display text-lg text-brand-ink placeholder:text-brand-muted/50"
                  placeholder="La tua migliore email"
                />
              </div>

              <label className="flex items-start gap-3 pt-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consenso}
                  onChange={(e) => setConsenso(e.target.checked)}
                  data-testid="lm-consenso"
                  className="mt-0.5 accent-brand-ink"
                />
                <span className="text-xs text-brand-muted leading-relaxed group-hover:text-brand-ink transition-colors">
                  Acconsento a ricevere occasionali comunicazioni sulle risorse e novità di Gorla Properties. Puoi disiscriverti in ogni momento.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                data-testid="lm-submit"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-brand-ink text-white font-medium px-6 py-3.5 rounded-sm hover:bg-brand-inkHover transition-all disabled:opacity-60"
              >
                {loading ? "Preparo il download..." : "Ricevi i file"}
                {!loading && <ArrowRight size={16} />}
              </button>

              <div className="pt-1 flex items-center gap-2 text-[11px] text-brand-muted justify-center">
                <ShieldCheck size={11} /> I tuoi dati sono al sicuro · niente spam
              </div>
            </form>
          </>
        ) : (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-brand-ink text-brand-beige flex items-center justify-center">
              <CheckCircle2 size={28} strokeWidth={1.5} />
            </div>
            <h3 className="mt-6 font-display text-3xl font-black text-brand-ink leading-tight">
              I file sono <span className="italic font-normal">pronti.</span>
            </h3>
            <p className="mt-3 text-sm text-brand-muted">
              Ho inviato tutto anche alla tua email — clicca qui sotto per scaricare subito.
            </p>
            <div className="mt-6 space-y-3">
              <a
                href={done.pdf}
                download
                data-testid="dl-pdf"
                className="w-full inline-flex items-center justify-center gap-3 bg-brand-ink text-white font-medium px-6 py-3.5 rounded-sm hover:bg-brand-inkHover transition-colors"
              >
                <FileText size={16} /> Scarica la guida PDF
              </a>
              <a
                href={done.xlsx}
                download
                data-testid="dl-xlsx"
                className="w-full inline-flex items-center justify-center gap-3 bg-brand-beige text-brand-ink font-medium px-6 py-3.5 rounded-sm hover:bg-brand-beigeDark transition-colors"
              >
                <Sheet size={16} /> Scarica il calcolatore Excel
              </a>
            </div>
            <button
              onClick={onClose}
              className="mt-6 text-xs text-brand-muted hover:text-brand-ink transition-colors underline underline-offset-2"
            >
              Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------ RESOURCE CARD ------------------ */
const ResourceCard = ({ resource, onClick }) => {
  const disabled = !resource.available;
  return (
    <div
      data-testid={`resource-${resource.id}`}
      className={`group relative flex flex-col rounded-sm overflow-hidden border transition-all duration-300 ${
        disabled
          ? "bg-brand-beige/20 border-brand-line opacity-80"
          : "bg-white border-brand-line hover:border-brand-ink hover:shadow-xl hover:-translate-y-1"
      }`}
    >
      {/* Header */}
      <div className={`${disabled ? "bg-brand-beige/40" : "bg-brand-ink"} text-white px-7 pt-7 pb-8 relative overflow-hidden`}>
        {!disabled && (
          <div
            aria-hidden
            className="absolute -right-6 -bottom-8 font-display italic font-black leading-none pointer-events-none select-none"
            style={{ fontSize: "180px", color: "rgba(228, 220, 207, 0.08)" }}
          >
            {resource.id.startsWith("guida") ? "€" : "?"}
          </div>
        )}
        <div className={`relative flex items-center justify-between`}>
          <div
            className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${
              disabled ? "text-brand-ink/60" : "text-brand-beige"
            }`}
          >
            {resource.kicker}
          </div>
          <div
            className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-sm ${
              resource.badge === "Gratis"
                ? "bg-brand-beige text-brand-ink"
                : "bg-brand-ink/70 text-brand-beige border border-brand-beige/40"
            }`}
          >
            {resource.badge}
          </div>
        </div>
        <h3
          className={`relative mt-4 font-display font-black leading-[1.02] ${
            disabled ? "text-brand-ink" : "text-white"
          } text-3xl md:text-4xl`}
        >
          {resource.title}
          <br />
          <span className="italic font-normal">{resource.titleItalic}</span>
        </h3>
      </div>

      {/* Body */}
      <div className="px-7 pt-6 pb-7 flex flex-col flex-1 gap-5">
        <p className="text-sm text-brand-muted leading-relaxed">{resource.description}</p>

        {/* File chips */}
        <div className="flex flex-wrap gap-2">
          {resource.files.map((f) => (
            <div
              key={f.label}
              className="inline-flex items-center gap-2 px-3 py-2 bg-brand-bg border border-brand-line rounded-sm text-xs"
            >
              <f.icon size={13} className="text-brand-ink" strokeWidth={1.8} />
              <span className="font-semibold text-brand-ink">{f.label}</span>
              {f.size && <span className="text-brand-muted">· {f.size}</span>}
            </div>
          ))}
        </div>

        {/* Highlights */}
        <ul className="space-y-2 mt-1">
          {resource.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 text-sm text-brand-ink">
              <CheckCircle2 size={14} strokeWidth={1.8} className="mt-0.5 text-brand-ink shrink-0" />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        <div className="flex-1" />

        <div className="flex items-center justify-between pt-4 border-t border-brand-line">
          <div>
            {resource.price && (
              <div className="font-display text-2xl font-bold text-brand-ink leading-none">
                {resource.price}
              </div>
            )}
            {!resource.price && (
              <div className="uppercase text-[10px] tracking-[0.28em] font-semibold text-brand-muted">
                Download istantaneo
              </div>
            )}
          </div>
          <button
            onClick={() => onClick(resource)}
            disabled={disabled}
            data-testid={`cta-${resource.id}`}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-sm font-medium text-sm transition-all ${
              disabled
                ? "bg-brand-beige/50 text-brand-muted cursor-not-allowed"
                : "bg-brand-ink text-white hover:bg-brand-inkHover"
            }`}
          >
            {disabled ? <Lock size={14} /> : <Download size={14} />}
            {resource.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------ MAIN PAGE ------------------ */
export const Resources = () => {
  const [activeResource, setActiveResource] = useState(null);

  const openGate = (resource) => {
    if (!resource.available) {
      toast.info("Presto disponibile. Iscriviti alla newsletter per essere avvisata all'uscita.");
      return;
    }
    setActiveResource(resource);
  };

  return (
    <div data-testid="resources-page" className="min-h-screen bg-brand-bg text-brand-text font-sans">
      {/* Header */}
      <header className="border-b border-brand-line/70 bg-brand-bg/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" data-testid="res-home" className="text-sm text-brand-muted hover:text-brand-ink transition-colors">
            ← Torna al sito
          </a>
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-muted">
            Guide & Risorse
          </div>
          <div className="text-sm text-brand-muted hidden md:block">gorlaproperties.com</div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-14">
        <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-brand-muted">
          <span className="inline-block align-middle h-px bg-brand-ink/40 w-10 mr-3" />
          Strumenti per proprietari
        </div>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] text-brand-ink max-w-3xl">
          Guide, calcolatori
          <br />
          <span className="italic font-normal">e strumenti</span> per decidere meglio.
        </h1>
        <p className="mt-7 text-brand-muted max-w-2xl text-base leading-relaxed">
          Qui trovi <strong className="text-brand-ink">materiali scaricabili</strong> pensati per proprietari immobiliari che vogliono capire meglio la fiscalità, calcolare la rendita netta e confrontare gli scenari di locazione.
        </p>
        <p className="mt-4 text-brand-muted max-w-2xl text-base leading-relaxed">
          Alcuni sono <strong className="text-brand-ink">gratuiti</strong> (guide, calcolatori base), altri saranno <strong className="text-brand-ink">a pagamento</strong> (playbook operativi, fac-simili di contratti, corsi). Ogni risorsa è segnalata con un badge chiaro.
        </p>

        {/* Meta stats */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "PDF", v: "Guide fiscali" },
            { k: "Excel", v: "Calcolatori interattivi" },
            { k: "Playbook", v: "Operativi (prossimamente)" },
            { k: "1:1", v: "Consulenze on-demand" },
          ].map((s) => (
            <div key={s.v} className="bg-white border border-brand-line rounded-sm px-4 py-3">
              <div className="font-display font-bold text-brand-ink text-lg leading-none">{s.k}</div>
              <div className="mt-1 text-[11px] text-brand-muted leading-tight uppercase tracking-[0.14em]">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Resources grid */}
      <section className="max-w-6xl mx-auto px-6 pb-14">
        <div className="flex items-baseline justify-between mb-8 border-t border-brand-line pt-8">
          <h2 className="font-display text-2xl font-bold text-brand-ink">
            Le risorse disponibili
          </h2>
          <span className="text-[11px] uppercase tracking-[0.28em] text-brand-muted flex items-center gap-2">
            <Sparkles size={11} /> Aggiornate al 2026
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {resources.map((r) => (
            <ResourceCard key={r.id} resource={r} onClick={openGate} />
          ))}
        </div>
      </section>

      {/* CTA finale */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="bg-brand-ink text-white rounded-sm p-10 md:p-14 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -right-10 -bottom-14 font-display italic font-black leading-none pointer-events-none select-none"
            style={{ fontSize: "280px", color: "rgba(228, 220, 207, 0.06)" }}
          >
            FG
          </div>
          <div className="relative max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-brand-beige">
              <span className="inline-block align-middle h-px bg-brand-beige/70 w-10 mr-2" />
              Vuoi una consulenza dedicata?
            </div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl font-black leading-[1.05]">
              Ogni immobile è diverso.
              <br />
              <span className="italic font-normal">Parliamone.</span>
            </h3>
            <p className="mt-5 text-white/75 leading-relaxed">
              I calcolatori danno una fotografia. La strategia giusta nasce dal confronto sul tuo caso specifico. Prenota una call di 30 minuti — è gratuita.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={site.calendly}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="res-calendly"
                className="inline-flex items-center gap-2 bg-brand-beige text-brand-ink font-medium px-6 py-3.5 rounded-sm hover:bg-white transition-colors"
              >
                Prenota una call gratuita <ArrowRight size={15} />
              </a>
              <a
                href={site.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="res-whatsapp"
                className="inline-flex items-center gap-2 bg-transparent border border-brand-beige/40 text-brand-beige font-medium px-6 py-3.5 rounded-sm hover:bg-white/5 transition-colors"
              >
                Scrivimi su WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-brand-line py-8 text-center text-xs text-brand-muted">
        Gorla Properties · Guide & Risorse
      </footer>

      {activeResource && (
        <EmailGate resource={activeResource} onClose={() => setActiveResource(null)} />
      )}
    </div>
  );
};

export default Resources;
